import React, { useState, useRef, useEffect } from "react";

interface DateRangePickerProps {
  dateFrom?: string;
  dateTo?: string;
  onChange: (dateFrom: string | undefined, dateTo: string | undefined) => void;
  placeholder?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateFrom,
  dateTo,
  onChange,
  placeholder = "Select date range"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localDateFrom, setLocalDateFrom] = useState(dateFrom || "");
  const [localDateTo, setLocalDateTo] = useState(dateTo || "");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalDateFrom(dateFrom || "");
    setLocalDateTo(dateTo || "");
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const datePresets = [
    {
      label: "Today",
      getValue: () => {
        const today = new Date().toISOString().split('T')[0];
        return { from: today, to: today };
      }
    },
    {
      label: "Last 7 days",
      getValue: () => {
        const today = new Date();
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          from: weekAgo.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      }
    },
    {
      label: "Last 30 days",
      getValue: () => {
        const today = new Date();
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return {
          from: monthAgo.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      }
    },
    {
      label: "Last 90 days",
      getValue: () => {
        const today = new Date();
        const threeMonthsAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
        return {
          from: threeMonthsAgo.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      }
    },
  ];

  const formatDisplayDate = (from?: string, to?: string) => {
    if (!from && !to) return placeholder;

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    if (from && to) {
      if (from === to) {
        return formatDate(from);
      }
      return `${formatDate(from)} - ${formatDate(to)}`;
    }
    if (from) return `From ${formatDate(from)}`;
    if (to) return `To ${formatDate(to)}`;
    return placeholder;
  };

  const handlePresetClick = (preset: any) => {
    const { from, to } = preset.getValue();
    setLocalDateFrom(from);
    setLocalDateTo(to);
    onChange(from, to);
    setIsOpen(false);
  };

  const handleApply = () => {
    onChange(
      localDateFrom || undefined,
      localDateTo || undefined
    );
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalDateFrom("");
    setLocalDateTo("");
    onChange(undefined, undefined);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", minWidth: "200px" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #e2e8f0",
          borderRadius: "4px",
          backgroundColor: "#fff",
          cursor: "pointer",
          textAlign: "left",
          fontSize: "14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: dateFrom || dateTo ? "#333" : "#999",
        }}
      >
        <span style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          {formatDisplayDate(dateFrom, dateTo)}
        </span>
        <span style={{ marginLeft: "8px", fontSize: "12px" }}>📅</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            minWidth: "320px",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            padding: "12px",
          }}
        >
          {/* Quick presets */}
          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "12px", fontWeight: "500", color: "#666", marginBottom: "8px" }}>
              Quick Select
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {datePresets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  style={{
                    padding: "4px 8px",
                    fontSize: "12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f4ff";
                    e.currentTarget.style.borderColor = "#5d5ded";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date inputs */}
          <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "12px" }}>
            <div style={{ fontSize: "12px", fontWeight: "500", color: "#666", marginBottom: "8px" }}>
              Custom Range
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                  From
                </label>
                <input
                  type="date"
                  value={localDateFrom}
                  onChange={(e) => setLocalDateFrom(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    fontSize: "13px",
                  }}
                />
              </div>
              <span style={{ color: "#999", paddingTop: "14px" }}>-</span>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                  To
                </label>
                <input
                  type="date"
                  value={localDateTo}
                  onChange={(e) => setLocalDateTo(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    fontSize: "13px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "12px",
            paddingTop: "12px",
            borderTop: "1px solid #e2e8f0"
          }}>
            <button
              onClick={handleClear}
              style={{
                padding: "6px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              style={{
                padding: "6px 16px",
                border: "none",
                borderRadius: "4px",
                backgroundColor: "#5d5ded",
                color: "#fff",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;